import { Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "src/redux/hooks";

import { HeaderBadge } from "../Header.styled";
import LogoutModal from "./LogoutModal";
import {
  getRemainingMs,
  getSessionLogoutDuration,
  HANDOVER_SESSION_KEY,
  LOGOUT_TIMER_REFRESH,
  LOGOUT_TIMER_START_KEY,
  refreshLogoutTimer,
} from "src/generator/utils/refreshLogoutTimer";

const LogoutTimer = () => {
  const [rerender, setRerender] = useState(0);
  const [timer, setTimer] = useState(0);
  const [opened, { open, close }] = useDisclosure();
  const logoutTime = useSelector(state => state.preferencesSlice.logoutTime);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (opened) return;

    const recalc = () => setRerender(prev => prev + 1);
    const resetTimerAndRecalc = () => {
      refreshLogoutTimer();
      recalc();
    };

    const sessionLogoutDuration = getSessionLogoutDuration(logoutTime);
    const startRaw = localStorage.getItem(LOGOUT_TIMER_START_KEY);

    if (!startRaw) {
      resetTimerAndRecalc();
      return;
    }

    const startMs = Number(startRaw);
    if (!Number.isFinite(startMs) || startMs <= 0) {
      resetTimerAndRecalc();
      return;
    }

    const time = getRemainingMs(startMs, sessionLogoutDuration);

    if (time <= 0) {
      setTimer(0);
      open();
      return;
    }

    if (time > 1000) {
      setTimer(time);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            open();
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LOGOUT_TIMER_START_KEY || e.key === HANDOVER_SESSION_KEY) {
        recalc();
      }
    };

    window.addEventListener("focus", recalc);
    window.addEventListener(LOGOUT_TIMER_REFRESH, recalc);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (opened) close();
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("focus", recalc);
      window.removeEventListener(LOGOUT_TIMER_REFRESH, recalc);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [close, logoutTime, open, opened, rerender]);

  const getTimer = () => {
    const totalSeconds = Math.floor(timer / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    let seconds: number | string = totalSeconds - minutes * 60;
    seconds = seconds < 10 ? "0" + seconds : seconds;
    return `${minutes}:${seconds}`;
  };

  const getColor = () => {
    const secondsLeft = Math.floor(timer / 1000);
    if (secondsLeft < 60) {
      return "red.9";
    } else if (secondsLeft < 60 * 5) {
      return "yellow.9";
    } else {
      return "green.9";
    }
  };

  return (
    <>
      <Tooltip label="Session Timeout" openDelay={300}>
        <HeaderBadge
          size="lg"
          variant="light"
          color={getColor()}
          my="auto"
          style={{ userSelect: "none" }}>
          {getTimer()}
        </HeaderBadge>
      </Tooltip>
      <LogoutModal opened={opened} close={close} />
    </>
  );
};

export default LogoutTimer;
